import { siteAssets } from '../siteAssets';

const playPeek = {
  slug: 'playpeek',
  title: 'PlayPeek',
  summary: 'I’m building a compact macOS media companion that keeps what’s playing within reach. PlayPeek is in active development, with launch expected in late November 2026.',
  image: siteAssets.playPeekCover,
  imageAlt: 'PlayPeek app icon with an orange play button over a silver mini player',
  featured: false,
  isUnderConstruction: false,
  discipline: 'Product design · macOS development',
  caseStudyLabel: 'Product development case study',
  status: {
    label: 'In active development',
    detail: 'Expected launch: late November 2026. Screenshots show the v0.4 development build; features and appearance may change before release.',
  },
  context: [
    { label: 'My focus', value: 'Product design and app development' },
    { label: 'Platform', value: 'macOS' },
    { label: 'Current stage', value: 'Active development · v0.4 preview' },
    { label: 'Expected launch', value: 'Late November 2026' },
  ],
  skills: ['macOS App Development', 'Interaction Design', 'Interface Design', 'Localization', 'Product Iteration'],
  contribution: 'I’m designing and developing PlayPeek around one everyday interaction: checking what’s playing without losing my place in what I’m doing. My work connects the floating mini player with the settings around it, from appearance controls to listening history and language selection. The current build brings those pieces together, and I’m continuing to refine the experience ahead of launch.',
  problem: 'Music can stay in the background while the rest of the desktop gets busy. Checking a song or pausing playback should be a small action, but switching back to a media app can interrupt the task at hand. I’m building PlayPeek to keep those everyday controls close by, with a compact overlay that gives the current track a place on the desktop.',
  process: [
    'Start with the moment of use. I centered the experience on a quick glance at the current track and a short path to playback controls. That focus gives the mini player a clear job and helps me decide what belongs in it.',
    'Separate everyday actions from setup. The overlay presents artwork, track information, playback controls, and progress. A separate settings window holds the choices that need more room, including appearance, behavior, language, and listening history.',
    'Build out the supporting experience. The v0.4 interface includes playing and paused states, appearance controls, a listening-history view, and English and Spanish settings. These screens show how the product is taking shape beyond the overlay itself.',
    'Keep the release in view. PlayPeek is still in active development. I’m using the current build as the starting point for further refinement, with a public launch expected in late November 2026.',
  ],
  decision: [
    { type: 'heading', text: 'A small player with a clear job' },
    'I kept the mini player focused on what matters in the moment: artwork, the song and artist, playback controls, and progress. Playing and paused states share the same layout so the controls stay in a familiar place. Longer titles have limited room, making the balance between a compact footprint and readable track information an ongoing consideration.',
    {
      type: 'image',
      title: 'The mini player during playback',
      src: siteAssets.playPeekMiniPlayer,
      alt: 'PlayPeek floating mini player showing album artwork, track information, playback controls, and a progress bar',
      width: 812,
      height: 284,
      fullSize: true,
      caption: 'v0.4 development preview: the current track and its main controls share one compact overlay.',
    },
    {
      type: 'image',
      title: 'The same player, paused',
      src: siteAssets.playPeekMiniPlayerPaused,
      alt: 'PlayPeek mini player in its paused state with a play button in the playback controls',
      width: 812,
      height: 284,
      fullSize: true,
      caption: 'The paused state preserves the layout while changing the available playback action.',
    },
    { type: 'heading', text: 'Make room for different desktops' },
    'An overlay shares space with everything else someone is doing. I placed its appearance controls in a dedicated settings view, where people can adjust opacity, corner radius, playback-bar styling, and the artwork glow. Keeping these choices out of the player leaves more room for the track itself.',
    {
      type: 'image',
      title: 'Appearance without crowding playback',
      src: siteAssets.playPeekAppearance,
      alt: 'PlayPeek appearance settings with playback bar color, shadow, artwork glow, opacity, and corner radius controls',
      width: 1664,
      height: 1504,
      fullSize: true,
      caption: 'v0.4 appearance settings bring visual adjustments into one place, including an option to restore the defaults.',
    },
    { type: 'heading', text: 'Give listening history its own space' },
    'Looking back through songs is a different task from checking the current one. I gave listening history a separate view with search, recorded songs, and an approval queue. The interface explains local storage and offers controls for collection and approval before adding a track, making those choices visible alongside the history.',
    {
      type: 'image',
      title: 'A closer look at listening history',
      src: siteAssets.playPeekHistory,
      alt: 'PlayPeek listening history with song search, collection and approval settings, saved songs, and an approval queue',
      width: 1664,
      height: 1504,
      fullSize: true,
      caption: 'v0.4 development preview: history has room for search and review without expanding the floating player.',
    },
    { type: 'heading', text: 'Build language into the experience' },
    'The current settings include English and Spanish interfaces. Placing the language selector in General makes it part of the main setup experience. Comparing the two also highlights details to refine before release: longer translated labels need enough space to stay understandable.',
  ],
  artifacts: [
    {
      title: 'General settings in English',
      src: siteAssets.playPeekGeneralEnglish,
      alt: 'PlayPeek General settings in English with the application language selector and update and developer links',
      width: 1664,
      height: 1504,
      fullSize: true,
      caption: 'The English interface establishes the settings structure and language selection.',
    },
    {
      title: 'General settings in Spanish',
      src: siteAssets.playPeekGeneralSpanish,
      alt: 'PlayPeek General settings in Spanish with translated navigation, language selection, and developer links',
      width: 1664,
      height: 1504,
      fullSize: true,
      caption: 'The Spanish interface follows the same structure; longer navigation labels reveal layout details to refine.',
    },
  ],
  evidence: 'The v0.4 screenshots document the current development interface, including the mini player, appearance controls, listening history, and language settings. They show the product’s direction, but do not establish usability or compatibility across media apps. PlayPeek has not launched yet, so there are no public release results to report.',
  nextStep: 'Before the expected late-November 2026 launch, I want to check how clearly the player communicates playback state, how comfortably it fits into a busy desktop, and whether history and language settings are easy to understand. Long track titles and translated labels are concrete cases to review as I continue refining the build. Release updates will be shared through the PlayPeek website and @PlayPeekApp.',
  outcomes: [
    'A v0.4 development build showing a compact media overlay and its playing and paused states.',
    'Supporting settings for appearance, listening history, and English and Spanish interfaces.',
    'An existing coming-soon website for release updates, with launch expected in late November 2026.',
  ],
  links: [
    { label: 'Visit PlayPeek · Coming soon', href: 'https://playpeek.dmcer.org' },
    { label: 'Follow development updates', href: 'https://x.com/PlayPeekApp' },
  ],
};

export default playPeek;
